// =============================================================================
// domain/coachTrend.ts — "vs prior 30 days", the period-over-period strip.
// -----------------------------------------------------------------------------
// PORTED FROM `tradecoach-saas/app/index.html:42140` — semantics, not code (D3).
//
// ── 🚨 A DELTA IS A CLAIM ABOUT TWO POPULATIONS, SO BOTH HAVE TO BE REAL ──
//
// The old client will not draw this at all below five trades in the prior window
// (`prevTrades.length >= 5`), and the floor is the whole reason the strip is
// trustworthy. Three trades last month and a good week this month produces
// "Win rate 67% ▲34" — a sentence about noise, printed in the same type as the
// real ones, on a screen a trader uses to decide what to change.
//
// ⚠ THE FLOOR IS ON THE PRIOR WINDOW ONLY, deliberately, and it is not symmetric
// by accident: the CURRENT window is already on screen above this strip with its
// own sample size beside it. What the reader cannot see is how thin the thing
// being compared against was.
//
// ── 🚨 ONE ROW READS BACKWARDS, AND IT IS THE ONE THAT MATTERS MOST ──
//
// Risk of ruin FALLING is the good direction. Every other row here is better
// when it rises. Colouring ruin like the rest would paint a book that got safer
// in the same red as one that started losing money — see `inverse` below.
// =============================================================================

/** The fields a headline is computed from — deliberately narrower than a trade. */
export interface TrendTrade {
  netPnl?: number | null;
  rrRealized?: number | null;
}

export interface CoachHeadline {
  /** Trades in the window. */
  n: number;
  /**
   * Percentage of DECIDED trades that won.
   *
   * ⚠ Scratches (exactly zero) are excluded from the denominator, matching every
   * other win rate in this client. Counting them as losses reports a worse
   * trader than the book does.
   */
  winRatePct: number | null;
  /** Mean realised R over the trades that HAVE one — null when none do. */
  avgR: number | null;
  /** Net P&L in dollars. */
  net: number;
}

const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function coachHeadline(trades: readonly TrendTrade[]): CoachHeadline {
  let wins = 0;
  let losses = 0;
  let net = 0;
  const rs: number[] = [];
  for (const t of trades) {
    const p = numOrNull(t.netPnl) ?? 0;
    net += p;
    if (p > 0) wins += 1;
    else if (p < 0) losses += 1;
    const r = numOrNull(t.rrRealized);
    if (r !== null) rs.push(r);
  }
  const decided = wins + losses;
  return {
    n: trades.length,
    winRatePct: decided ? (wins / decided) * 100 : null,
    avgR: rs.length ? rs.reduce((a, b) => a + b, 0) / rs.length : null,
    net,
  };
}

/** How a row's number should be written. The component owns the formatting. */
export type TrendUnit = 'pct' | 'r' | 'money';

export interface TrendRow {
  /** Stable across renders, so a test and a `key` can both name a row. */
  id: 'winRate' | 'avgR' | 'ruin' | 'net';
  label: string;
  unit: TrendUnit;
  value: number;
  /**
   * Signed change against the prior window, or null when there is nothing
   * comparable — the prior window could not produce this number at all, or it
   * produced the same one.
   */
  delta: number | null;
  /** Whether the move was in the trader's favour. Null when `delta` is null. */
  good: boolean | null;
}

/** The old client's floor before a comparison is drawn at all. */
export const TREND_MIN_PRIOR_TRADES = 5;

/**
 * The strip's rows, or null when there is nothing honest to compare.
 *
 * ⚠ RETURNS NULL RATHER THAN AN EMPTY ARRAY when the floor is not met, because
 * the two mean different things to the caller: null is "draw no strip", and an
 * empty array would be a strip with a "vs prior 30 days:" label and nothing
 * after it.
 *
 * ⚠ A ROW WITH NO CURRENT VALUE IS DROPPED, not drawn as an em dash. The old
 * client does the same (`if (cur == null) return`). A comparison row whose
 * present half is unknown has nothing to say — and "Avg R — ▲2.1" would read as
 * though the missing half were zero.
 */
export function coachTrendRows(args: {
  current: CoachHeadline;
  prior: CoachHeadline;
  /** Percent, as displayed. Null when ruin could not be modelled. */
  ruinPct: number | null;
  priorRuinPct: number | null;
}): TrendRow[] | null {
  if (args.prior.n < TREND_MIN_PRIOR_TRADES) return null;

  const rows: TrendRow[] = [];
  const seg = (
    id: TrendRow['id'], label: string, unit: TrendUnit,
    cur: number | null, prev: number | null, inverse = false,
  ): void => {
    if (cur === null || !Number.isFinite(cur)) return;
    // ⚠ THE EPSILON IS THE OLD CLIENT'S AND IT IS LOAD-BEARING. Floating-point
    // sums of the same dollars differ in the fifteenth decimal, so an exact
    // `!==` puts a ▲0 on a book that did not move.
    if (prev !== null && Number.isFinite(prev) && Math.abs(cur - prev) > 1e-9) {
      const d = cur - prev;
      rows.push({ id, label, unit, value: cur, delta: d, good: inverse ? d < 0 : d > 0 });
    } else {
      rows.push({ id, label, unit, value: cur, delta: null, good: null });
    }
  };

  seg('winRate', 'Win rate', 'pct', args.current.winRatePct, args.prior.winRatePct);
  seg('avgR', 'Avg R', 'r', args.current.avgR, args.prior.avgR);
  seg('ruin', 'Risk of ruin', 'pct', args.ruinPct, args.priorRuinPct, true);
  seg('net', 'Net', 'money', args.current.net, args.prior.net);

  return rows.length ? rows : null;
}
